import { Button, type ContextMenuItem, DataTable } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Ban, CircleCheck, KeySquare, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type OAuthClientCreated, type OAuthClientResponse, platformListOauthClientsOptions } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { RowMenu } from "@/components/build/RowMenu";
import { Text } from "@/components/Text";
import { AdminPage } from "../AdminPage";
import { useAdminCanEdit } from "../useAdminCanEdit";
import { OAuthClientFormModal } from "./OAuthClientFormModal";
import { OAuthClientSecretModal } from "./OAuthClientSecretModal";
import { useOAuthClientColumns } from "./oauthClientColumns";
import { useOAuthClientActions } from "./useOAuthClientActions";

type Editing = { mode: "create" } | { mode: "edit"; client: OAuthClientResponse } | null;

/** /admin/platform/oauth-clients: apps that sign in through the Anthm issuer. */
export function OAuthClientsPage() {
  const { t } = useTranslation("adminPlatform");
  const canEdit = useAdminCanEdit();
  const clients = useQuery(platformListOauthClientsOptions());
  const columns = useOAuthClientColumns();
  const actions = useOAuthClientActions();
  const [editing, setEditing] = useState<Editing>(null);
  const [created, setCreated] = useState<OAuthClientCreated | null>(null);

  const rowMenu = (client: OAuthClientResponse) => {
    const items: ContextMenuItem[] = [
      {
        label: t("oauthClients.menu.edit"),
        icon: <Pencil size={16} />,
        onSelect: () => setEditing({ mode: "edit", client }),
      },
      client.isEnabled
        ? {
            label: t("oauthClients.menu.disable"),
            icon: <Ban size={16} />,
            onSelect: () => actions.setEnabled(client, false),
          }
        : {
            label: t("oauthClients.menu.enable"),
            icon: <CircleCheck size={16} />,
            onSelect: () => actions.setEnabled(client, true),
          },
    ];
    if (!client.isFirstParty) {
      items.push({
        label: t("oauthClients.menu.delete"),
        icon: <Trash2 size={16} />,
        danger: true,
        onSelect: () => actions.requestDelete(client),
      });
    }
    return <RowMenu name={client.clientName} items={items} />;
  };

  return (
    <AdminPage
      title={t("oauthClients.title")}
      subtitle={t("oauthClients.subtitle")}
      actions={
        <Button variant="primary" leadingIcon={<Plus size={16} />} onClick={() => setEditing({ mode: "create" })}>
          {t("oauthClients.new")}
        </Button>
      }
    >
      {clients.isError ? (
        <Text tone="error" role="alert">
          {t("oauthClients.loadFailed")}
        </Text>
      ) : (
        <DataTable
          aria-label={t("oauthClients.tableLabel")}
          columns={columns}
          rows={clients.data?.data ?? []}
          rowKey={(client) => client.uuid}
          loading={clients.isPending}
          empty={
            <EmptyState
              icon={<KeySquare size={32} />}
              title={t("oauthClients.empty")}
              body={t("oauthClients.emptyBody")}
            />
          }
          renderActions={canEdit ? rowMenu : undefined}
          actionsLabel={t("oauthClients.columns.actions")}
        />
      )}
      {canEdit && editing && (
        <OAuthClientFormModal
          key={editing.mode === "edit" ? editing.client.uuid : "new"}
          client={editing.mode === "edit" ? editing.client : undefined}
          onClose={() => setEditing(null)}
          onCreated={setCreated}
        />
      )}
      <OAuthClientSecretModal created={created} onClose={() => setCreated(null)} />
      {actions.dialog}
    </AdminPage>
  );
}
